import { useState, useEffect, useMemo } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import paymentService from '../../services/paymentService';
import reportService from '../../services/reportService';
import torneoService from '../../services/torneoService';
import SeguroService from '../../services/seguroService';
import ScoutService from '../../services/scoutService';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import './Reports.css';

const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

const Reports = () => {
  const [activeTab, setActiveTab] = useState('financial');
  const [dateRange, setDateRange] = useState('all');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Datos reales
  const [financialData, setFinancialData] = useState(null);
  const [overviewData, setOverviewData] = useState(null);
  const [userActivityDetailedData, setUserActivityDetailedData] = useState(null);
  const [matchesData, setMatchesData] = useState(null);
  const [achievementsData, setAchievementsData] = useState(null);

  // Filtros para actividad de usuarios
  const [userFilters] = useState({
    role: '',
    hasPlayers: '',
    hasInscriptions: ''
  });

  // Vista activa en el tab de actividad de usuarios
  const [userActivityView, setUserActivityView] = useState('all'); // 'all', 'no-players', 'with-players', 'with-inscriptions', 'no-inscriptions', 'seguro'

  // Datos de futbolistas con seguro (cargados bajo demanda)
  const [seguroData, setSeguroData] = useState(null);
  const [seguroLoading, setSeguroLoading] = useState(false);

  // Datos de captación (cargados bajo demanda)
  const [captacionData, setCaptacionData] = useState(null);
  const [captacionLoading, setCaptacionLoading] = useState(false);

  // Buscador para actividad de usuarios
  const [searchTerm, setSearchTerm] = useState('');

  // Modal de futbolistas
  const [showFutbolistasModal, setShowFutbolistasModal] = useState(false);
  const [selectedTutorFutbolistas, setSelectedTutorFutbolistas] = useState(null);

  // Filtro de torneo para documentos
  const [torneos, setTorneos] = useState([]);
  const [selectedTorneoId, setSelectedTorneoId] = useState('');

  // Filtro de torneo para actividad de usuarios
  const [selectedTorneoIdActivity, setSelectedTorneoIdActivity] = useState('');

  // Filtro de sede dentro de la vista de estados
  const [selectedSedeFilter, setSelectedSedeFilter] = useState('');

  // Fechas calculadas según dateRange
  const { startDateISO, endDateISO } = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date();

    switch (dateRange) {
      case 'week':
        startDate.setDate(endDate.getDate() - 7);
        break;
      case 'month':
        startDate.setMonth(endDate.getMonth() - 1);
        break;
      case 'quarter':
        startDate.setMonth(endDate.getMonth() - 3);
        break;
      case 'year':
        startDate.setFullYear(endDate.getFullYear() - 1);
        break;
      case 'all':
      default:
        return { startDateISO: null, endDateISO: null };
    }
    return { startDateISO: startDate.toISOString(), endDateISO: endDate.toISOString() };
  }, [dateRange]);

  const commonParams = useMemo(() => {
    const params = {};
    if (dateRange !== 'all') {
      params.startDate = startDateISO;
      params.endDate = endDateISO;
    }
    return params;
  }, [dateRange, startDateISO, endDateISO]);

  // Cargar torneos para el filtro de documentos
  useEffect(() => {
    const loadTorneos = async () => {
      try {
        const response = await torneoService.getTorneos();
        setTorneos(response.data || []);
      } catch (error) {
        console.error('Error al cargar torneos:', error);
        toast.error('Error al cargar la lista de torneos');
      }
    };
    loadTorneos();
  }, []);

  // Carga de datos (financieros + resto)
  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const requests = [
          paymentService.getFinancialReports(commonParams),
          reportService.getOverview(commonParams),
          reportService.getUserActivityDetailed({ ...commonParams, ...userFilters, ...(selectedTorneoIdActivity && { torneo_id: selectedTorneoIdActivity }) }),
          reportService.getMatches(commonParams),
          reportService.getAchievements(commonParams),
        ];

        const [finRes, ovRes, uadRes, mtRes, acRes] = await Promise.allSettled(requests);

        if (!isMounted) return;

        if (finRes.status === 'fulfilled') setFinancialData(finRes.value.data);
        else setFinancialData(null);

        if (ovRes.status === 'fulfilled') setOverviewData(ovRes.value.data);
        else setOverviewData(null);

        // userActivityData ya no se usa, solo userActivityDetailedData
        if (uadRes.status === 'fulfilled') {
          console.log('✅ User Activity Detailed Response:', uadRes.value);
          // El backend devuelve { success: true, data: { stats, usuarios, ... } }
          // Así que necesitamos acceder a .data.data
          setUserActivityDetailedData(uadRes.value.data.data);
        } else {
          console.error('❌ User Activity Detailed Error:', uadRes.reason);
          setUserActivityDetailedData(null);
        }

        if (mtRes.status === 'fulfilled') setMatchesData(mtRes.value.data);
        else setMatchesData(null);

        if (acRes.status === 'fulfilled') setAchievementsData(acRes.value.data);
        else setAchievementsData(null);
      } catch (err) {
        console.error('Error cargando informes:', err);
        setError('Error al cargar los datos. Por favor, inténtalo de nuevo.');
        toast.error('Error al cargar los datos de informes');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();
    return () => {
      isMounted = false;
    };
  }, [commonParams, userFilters, selectedTorneoIdActivity]);

  // Handlers
  const handleTabChange = (tab) => setActiveTab(tab);
  const handleDateRangeChange = (e) => setDateRange(e.target.value);

  // Función para exportar a Excel
  const exportToExcel = (data, filename) => {
    // Eliminar el campo _originalData antes de exportar
    const cleanData = data.map(row => {
      const { _originalData, ...rest } = row;
      return rest;
    });
    const ws = XLSX.utils.json_to_sheet(cleanData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Datos');
    XLSX.writeFile(wb, `${filename}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Archivo Excel exportado correctamente');
  };

  // Función para mostrar futbolistas de un tutor
  const handleShowFutbolistas = (tutorEmail) => {
    if (!userActivityDetailedData || !userActivityDetailedData.futbolistas) {
      toast.error('No hay datos de futbolistas disponibles');
      return;
    }

    // Filtrar futbolistas del tutor seleccionado
    const futbolistasTutor = userActivityDetailedData.futbolistas.filter(
      f => f.tutor_email === tutorEmail
    );

    if (futbolistasTutor.length === 0) {
      toast.info('Este tutor no tiene futbolistas registrados');
      return;
    }

    setSelectedTutorFutbolistas({
      tutorEmail,
      tutorName: futbolistasTutor[0]?.tutor_name || tutorEmail,
      futbolistas: futbolistasTutor
    });
    setShowFutbolistasModal(true);
  };

  // Función para enviar emails masivos
  const handleSendBulkEmails = async (estado) => {
    // Obtener los usuarios según el estado
    let usuarios = [];
    const { usuarios_estado_1, usuarios_estado_2, usuarios_estado_3 } = userActivityDetailedData;

    switch (estado) {
      case 'estado-1':
        usuarios = usuarios_estado_1;
        break;
      case 'estado-2':
        usuarios = usuarios_estado_2;
        break;
      case 'estado-3':
        usuarios = usuarios_estado_3;
        break;
      default:
        toast.error('Estado no válido');
        return;
    }

    if (usuarios.length === 0) {
      toast.warning('No hay usuarios para enviar emails');
      return;
    }

    // Confirmar antes de enviar
    const estadoNombres = {
      'estado-1': 'Estado 1 (Sin futbolistas)',
      'estado-2': 'Estado 2 (Con futbolistas, sin compras)',
      'estado-3': 'Estado 3 (Solo jornadas pasadas)'
    };

    const confirmMessage = `¿Estás seguro de enviar emails a ${usuarios.length} usuarios de ${estadoNombres[estado]}?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setLoading(true);
      toast.info(`Enviando emails a ${usuarios.length} usuarios...`);

      const response = await reportService.sendBulkEmails(estado, usuarios);

      if (response.data.success) {
        toast.success(`✅ Emails enviados: ${response.data.data.sent} exitosos, ${response.data.data.failed} fallidos`);
      } else {
        toast.error('Error al enviar emails');
      }
    } catch (error) {
      console.error('Error enviando emails:', error);
      toast.error(error.response?.data?.message || 'Error al enviar emails masivos');
    } finally {
      setLoading(false);
    }
  };

  // ---------- RENDERIZADOS POR PESTAÑA (sin staticReportData) ----------

  const renderOverviewTab = () => {
    if (!overviewData) {
      return (
        <div className="report-section">
          <div className="loading-message">
            <p>No hay datos de resumen para el periodo seleccionado.</p>
          </div>
        </div>
      );
    }

    const {
      totalUsers = 0,
      activeUsers = 0,
      totalMatches = 0,
      completedMatches = 0,
      totalRevenue = 0,
      averageAttendance = 0,
      usersByCategory = [],
      matchesByCategory = [],
      incomeVsExpense = [],
      achievements = [],
    } = overviewData;

    return (
      <div className="report-section">
        <div className="stats-grid">
          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#bbdefb' }}>
              <span className="material-icons" style={{ color: '#1976d2' }}>people</span>
            </div>
            <div className="stats-info">
              <h3>Usuarios Totales</h3>
              <div className="stats-value">{totalUsers}</div>
              <div className="stats-detail">{activeUsers} activos ({totalUsers ? Math.round((activeUsers / totalUsers) * 100) : 0}%)</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#c8e6c9' }}>
              <span className="material-icons" style={{ color: '#4caf50' }}>sports_soccer</span>
            </div>
            <div className="stats-info">
              <h3>Partidos Totales</h3>
              <div className="stats-value">{totalMatches}</div>
              <div className="stats-detail">{completedMatches} completados ({totalMatches ? Math.round((completedMatches / totalMatches) * 100) : 0}%)</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#ffecb3' }}>
              <span className="material-icons" style={{ color: '#ff9800' }}>euro</span>
            </div>
            <div className="stats-info">
              <h3>Ingresos Totales</h3>
              <div className="stats-value">{new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(totalRevenue)}</div>
              <div className="stats-detail">{totalMatches ? `${Math.round(totalRevenue / totalMatches)} € por partido` : '—'}</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#f8bbd0' }}>
              <span className="material-icons" style={{ color: '#e91e63' }}>event_available</span>
            </div>
            <div className="stats-info">
              <h3>Asistencia Media</h3>
              <div className="stats-value">{averageAttendance}%</div>
              <div className="stats-detail">de los jugadores inscritos</div>
            </div>
          </div>
        </div>

        <div className="report-grid">
          <div className="report-card">
            <h3>Usuarios por Categoría</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de usuarios por categoría</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Categoría</th><th>Usuarios</th><th>%</th></tr></thead>
                <tbody>
                  {usersByCategory.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.name}</td>
                      <td>{item.value}</td>
                      <td>{totalUsers ? Math.round((item.value / totalUsers) * 100) : 0}%</td>
                    </tr>
                  ))}
                  {usersByCategory.length === 0 && <tr><td colSpan="3" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Partidos por Categoría</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de partidos por categoría</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Categoría</th><th>Partidos</th><th>%</th></tr></thead>
                <tbody>
                  {matchesByCategory.map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.name}</td>
                      <td>{item.value}</td>
                      <td>{totalMatches ? Math.round((item.value / totalMatches) * 100) : 0}%</td>
                    </tr>
                  ))}
                  {matchesByCategory.length === 0 && <tr><td colSpan="3" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Ingresos vs Gastos</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de ingresos vs gastos</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Mes</th><th>Ingresos</th><th>Gastos</th></tr></thead>
                <tbody>
                  {incomeVsExpense.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.month}</td>
                      <td>{new Intl.NumberFormat('es-ES', { style:'currency', currency:'EUR' }).format(row.income || 0)}</td>
                      <td>{new Intl.NumberFormat('es-ES', { style:'currency', currency:'EUR' }).format(row.expense || 0)}</td>
                    </tr>
                  ))}
                  {incomeVsExpense.length === 0 && <tr><td colSpan="3" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Logros Desbloqueados</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de logros desbloqueados</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Logro</th><th>Usuarios</th></tr></thead>
                <tbody>
                  {achievements.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.name}</td>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                  {achievements.length === 0 && <tr><td colSpan="2" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderUserActivityTab = () => {
    if (loading || !userActivityDetailedData) {
      return (
        <div className="report-section">
          <div className="loading-message"><p>Cargando datos de actividad de usuarios...</p></div>
        </div>
      );
    }

    if (!userActivityDetailedData.stats) {
      return (
        <div className="report-section">
          <div className="error-message">
            <p>Error al cargar los datos. Por favor, intenta recargar la página.</p>
          </div>
        </div>
      );
    }

    const { stats, todos_usuarios, usuarios_completos, usuarios_primera_inscripcion, usuarios_estado_1, usuarios_estado_2, usuarios_estado_3 } = userActivityDetailedData;

    // Función para filtrar usuarios por término de búsqueda
    const filterBySearchTerm = (usuarios) => {
      if (!searchTerm.trim()) return usuarios;

      const term = searchTerm.toLowerCase().trim();
      return usuarios.filter(u =>
        (u.name && u.name.toLowerCase().includes(term)) ||
        (u.email && u.email.toLowerCase().includes(term)) ||
        (u.phone && u.phone.toLowerCase().includes(term)) ||
        // Campos de seguro
        (u.futbolista_nombre && u.futbolista_nombre.toLowerCase().includes(term)) ||
        (u.tutor_nombre && u.tutor_nombre.toLowerCase().includes(term)) ||
        (u.tutor_email && u.tutor_email.toLowerCase().includes(term))
      );
    };

    // Determinar qué datos mostrar según la vista activa
    let dataToShow = [];
    let viewTitle = '';
    let exportFilename = '';

    switch (userActivityView) {
      case 'all':
        dataToShow = filterBySearchTerm(todos_usuarios)
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Ordenar por fecha de registro (más antiguo primero)
          .map(u => ({
            Nombre: u.name,
            Email: u.email,
            Teléfono: u.phone || 'N/A',
            Estado: u.estado || 'N/A',
            'Sede Preferida': u.sedes_por_defecto || u.sedes_futuras || u.sedes_compradas || u.sedes || 'N/A',
            'Nº Futbolistas': u.num_futbolistas !== undefined ? u.num_futbolistas : 'N/A',
            'Nº Inscripciones': u.num_inscripciones !== undefined ? u.num_inscripciones : 'N/A',
            Torneo: u.torneos || 'N/A',
            'Jornadas Restantes': u.jornadas_restantes !== null && u.jornadas_restantes !== undefined ? u.jornadas_restantes : 'N/A',
            'Saldo Mínimo': u.saldo_minimo !== null && u.saldo_minimo !== undefined ? u.saldo_minimo : 'N/A',
            'Última Compra': u.ultima_compra ? new Date(u.ultima_compra).toLocaleDateString('es-ES') : 'N/A',
            'Fin Inscripción': u.fin_inscripcion ? new Date(u.fin_inscripcion).toLocaleDateString('es-ES') : 'N/A',
            'Fecha Registro': new Date(u.createdAt).toLocaleDateString('es-ES'),
            _originalData: u // Guardar datos originales para el modal
          }));
        viewTitle = 'Todos los Usuarios Tutores';
        exportFilename = 'todos_usuarios_tutores';
        break;
      case 'completos':
        dataToShow = filterBySearchTerm(usuarios_completos)
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Ordenar por fecha de registro (más antiguo primero)
          .map(u => ({
            Nombre: u.name,
            Email: u.email,
            Teléfono: u.phone || 'N/A',
            Estado: u.estado,
            Sedes: u.sedes || 'N/A',
            'Nº Futbolistas': u.num_futbolistas,
            'Nº Inscripciones': u.num_inscripciones,
            Torneo: u.torneos || 'N/A',
            'Jornadas Restantes': u.jornadas_restantes !== null ? u.jornadas_restantes : 'N/A',
            'Saldo Mínimo': u.saldo_minimo !== null ? u.saldo_minimo : 'N/A',
            'Última Compra': u.ultima_compra ? new Date(u.ultima_compra).toLocaleDateString('es-ES') : 'N/A',
            'Fin Inscripción': u.fin_inscripcion ? new Date(u.fin_inscripcion).toLocaleDateString('es-ES') : 'N/A',
            'Fecha Registro': new Date(u.createdAt).toLocaleDateString('es-ES'),
            _originalData: u
          }));
        viewTitle = 'Usuarios Completos (Con jornadas futuras)';
        exportFilename = 'usuarios_completos';
        break;
      case 'estado-1':
        dataToShow = filterBySearchTerm(usuarios_estado_1)
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Ordenar por fecha de registro (más antiguo primero)
          .map(u => ({
            Nombre: u.name,
            Email: u.email,
            Teléfono: u.phone || 'N/A',
            Estado: u.estado,
            Descripción: u.estado_descripcion,
            'Fecha Registro': new Date(u.createdAt).toLocaleDateString('es-ES'),
            _originalData: u
          }));
        viewTitle = 'Estado 1 - Sin Futbolistas';
        exportFilename = 'usuarios_estado_1_sin_futbolistas';
        break;
      case 'estado-2':
        dataToShow = filterBySearchTerm(usuarios_estado_2)
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Ordenar por fecha de registro (más antiguo primero)
          .map(u => ({
            Nombre: u.name,
            Email: u.email,
            Teléfono: u.phone || 'N/A',
            Estado: u.estado,
            'Sede Preferida': u.sedes || 'Sin sede',
            'Nº Futbolistas': u.num_futbolistas,
            Futbolistas: u.futbolistas_detalle || 'N/A',
            Descripción: u.estado_descripcion,
            'Fecha Registro': new Date(u.createdAt).toLocaleDateString('es-ES'),
            _originalData: u
          }));
        viewTitle = 'Estado 2 - Con futbolistas, sin compras';
        exportFilename = 'usuarios_estado_2_sin_compras';
        break;
      case 'estado-3':
        dataToShow = filterBySearchTerm(usuarios_estado_3)
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) // Ordenar por fecha de registro (más antiguo primero)
          .map(u => ({
            Nombre: u.name,
            Email: u.email,
            Teléfono: u.phone || 'N/A',
            Estado: u.estado,
            'Sedes Compradas': u.sedes || 'N/A',
            'Nº Futbolistas': u.num_futbolistas,
            'Nº Inscripciones': u.num_inscripciones,
            Torneo: u.torneos || 'N/A',
            'Saldo Mínimo': u.saldo_minimo !== null ? u.saldo_minimo : 'N/A',
            'Última Compra': u.ultima_compra ? new Date(u.ultima_compra).toLocaleDateString('es-ES') : 'N/A',
            'Última Jornada': u.ultima_jornada ? new Date(u.ultima_jornada).toLocaleDateString('es-ES') : 'N/A',
            Descripción: u.estado_descripcion,
            'Fecha Registro': new Date(u.createdAt).toLocaleDateString('es-ES'),
            _originalData: u
          }));
        viewTitle = 'Estado 3 - Solo Jornadas Pasadas';
        exportFilename = 'usuarios_estado_3_jornadas_pasadas';
        break;
      case 'primera-inscripcion':
        // Expandir cada tutor con sus futbolistas nuevos en filas separadas
        dataToShow = filterBySearchTerm(usuarios_primera_inscripcion || [])
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
          .flatMap(u => {
            // Si tiene futbolistas nuevos, crear una fila por cada futbolista
            if (u.futbolistas_nuevos && u.futbolistas_nuevos.length > 0) {
              return u.futbolistas_nuevos.map(f => ({
                'Tutor': u.name,
                'Email Tutor': u.email,
                'Teléfono Tutor': u.phone || 'N/A',
                'Sede Preferida': u.sedes_por_defecto || u.sedes_futuras || u.sedes_compradas || u.sedes || 'N/A',
                'Futbolista': f.nombre,
                'Fecha Creación Futbolista': new Date(f.fecha_creacion).toLocaleDateString('es-ES'),
                'Nº Total Futbolistas': u.num_futbolistas,
                'Nº Futbolistas Nuevos': u.num_futbolistas_nuevos || 0,
                'Nº Inscripciones': u.num_inscripciones,
                'Torneo': u.torneos || 'N/A',
                'Jornadas Restantes': u.jornadas_restantes !== null ? u.jornadas_restantes : 'N/A',
                'Saldo Mínimo': u.saldo_minimo !== null ? u.saldo_minimo : 'N/A',
                'Última Compra': u.ultima_compra ? new Date(u.ultima_compra).toLocaleDateString('es-ES') : 'N/A',
                'Fecha Registro Tutor': new Date(u.createdAt).toLocaleDateString('es-ES'),
                _originalData: u
              }));
            } else {
              // Si no tiene futbolistas nuevos (no debería pasar), mostrar solo el tutor
              return [{
                'Tutor': u.name,
                'Email Tutor': u.email,
                'Teléfono Tutor': u.phone || 'N/A',
                'Sede Preferida': u.sedes_por_defecto || u.sedes_futuras || u.sedes_compradas || u.sedes || 'N/A',
                'Futbolista': 'N/A',
                'Fecha Creación Futbolista': 'N/A',
                'Nº Total Futbolistas': u.num_futbolistas,
                'Nº Futbolistas Nuevos': u.num_futbolistas_nuevos || 0,
                'Nº Inscripciones': u.num_inscripciones,
                'Torneo': u.torneos || 'N/A',
                'Jornadas Restantes': u.jornadas_restantes !== null ? u.jornadas_restantes : 'N/A',
                'Saldo Mínimo': u.saldo_minimo !== null ? u.saldo_minimo : 'N/A',
                'Última Compra': u.ultima_compra ? new Date(u.ultima_compra).toLocaleDateString('es-ES') : 'N/A',
                'Fecha Registro Tutor': new Date(u.createdAt).toLocaleDateString('es-ES'),
                _originalData: u
              }];
            }
          });
        viewTitle = 'Primera Inscripción - Futbolistas Nuevos';
        exportFilename = 'usuarios_primera_inscripcion';
        break;
      case 'seguro':
        if (seguroLoading) {
          dataToShow = [];
          viewTitle = 'Futbolistas con Seguro (cargando...)';
          exportFilename = 'futbolistas_con_seguro';
        } else {
          dataToShow = filterBySearchTerm(seguroData || [])
            .map(f => ({
              Futbolista: f.futbolista_nombre || 'N/A',
              'Fecha Nacimiento': f.fecha_nacimiento ? new Date(f.fecha_nacimiento).toLocaleDateString('es-ES') : (f.ano_nacimiento || 'N/A'),
              'Sede Preferida': f.sede_preferida || 'N/A',
              Tutor: f.tutor_nombre || 'N/A',
              'Email Tutor': f.tutor_email || 'N/A',
              'Teléfono Tutor': f.tutor_telefono || 'N/A',
              'Fecha Compra': f.fecha_compra ? new Date(f.fecha_compra).toLocaleDateString('es-ES') : 'N/A',
              'Inicio Cobertura': f.fecha_inicio_cobertura ? new Date(f.fecha_inicio_cobertura).toLocaleDateString('es-ES') : 'N/A',
              'Fin Cobertura': f.fecha_fin_cobertura ? new Date(f.fecha_fin_cobertura).toLocaleDateString('es-ES') : 'N/A',
              'Precio Pagado': f.precio_pagado != null ? `${parseFloat(f.precio_pagado).toFixed(2)} €` : 'N/A',
              _originalData: f
            }));
          viewTitle = 'Futbolistas con Seguro Activo';
          exportFilename = 'futbolistas_con_seguro';
        }
        break;
      case 'captacion': {
        const origenLabels = {
          'scout': 'Scout',
          'recomendacion_futbolista': 'Recomendación de futbolista',
          'rrss': 'Redes Sociales',
          'web': 'Web / Publicidad Online',
          'evento_ap8': 'Evento AP8 Soccer',
          'otro': 'Otro',
          'desconocido': 'Desconocido'
        };
        if (captacionLoading) {
          dataToShow = [];
          viewTitle = 'Captación (cargando...)';
          exportFilename = 'origen_captacion';
        } else {
          dataToShow = filterBySearchTerm(captacionData || [])
            .map(f => ({
              Futbolista: f.nombre || 'N/A',
              'Año Nacimiento': f.ano_nacimiento || 'N/A',
              Sede: f.sede_nombre || 'N/A',
              'Origen': origenLabels[f.origen_captacion] || f.origen_captacion || 'N/A',
              'Detalle': f.origen_captacion === 'scout' ? (f.scout_nombre || 'N/A') : (f.origen_detalle || '-'),
              Tutor: f.tutor_nombre || 'N/A',
              'Email Tutor': f.tutor_email || 'N/A',
              'Teléfono Tutor': f.tutor_telefono || 'N/A',
              _originalData: f
            }));
          viewTitle = 'Captación';
          exportFilename = 'origen_captacion';
        }
        break;
      }
      default:
        dataToShow = [];
    }

    // Filtro de sede: extraer sedes únicas según la vista y filtrar
    const sedeFilterApplies = ['all', 'completos', 'estado-2', 'estado-3', 'primera-inscripcion'].includes(userActivityView);
    let sedesDisponibles = [];
    if (sedeFilterApplies) {
      const sedeField = userActivityView === 'completos' ? 'Sedes'
        : userActivityView === 'estado-3' ? 'Sedes Compradas'
        : 'Sede Preferida';
      const sedesSet = new Set();
      dataToShow.forEach(row => {
        const val = row[sedeField];
        if (val && val !== 'N/A' && val !== 'Sin sede' && val !== 'Sin sede asignada') {
          val.split(', ').forEach(s => sedesSet.add(s.trim()));
        }
      });
      sedesDisponibles = Array.from(sedesSet).sort();

      // Aplicar filtro si hay sede seleccionada
      if (selectedSedeFilter) {
        dataToShow = dataToShow.filter(row => {
          const val = row[sedeField];
          if (!val) return false;
          return val.split(', ').map(s => s.trim()).includes(selectedSedeFilter);
        });
      }
    }

    return (
      <div className="report-section">
        {/* Filtro de Torneo para Actividad de Usuarios */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label htmlFor="torneo-activity-filter" style={{ fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
            <span className="material-icons" style={{ fontSize: '20px', color: '#1976d2' }}>filter_list</span>
            Filtrar por Torneo:
          </label>
          <select
            id="torneo-activity-filter"
            value={selectedTorneoIdActivity}
            onChange={(e) => setSelectedTorneoIdActivity(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              fontSize: '14px',
              minWidth: '250px'
            }}
          >
            <option value="">📋 Todos los torneos</option>
            {torneos.map(torneo => (
              <option key={torneo.id} value={torneo.id}>
                {torneo.nombre}
              </option>
            ))}
          </select>
          {selectedTorneoIdActivity && (
            <button
              onClick={() => setSelectedTorneoIdActivity('')}
              style={{
                padding: '8px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✕ Limpiar filtro
            </button>
          )}
          {selectedTorneoIdActivity && stats.total_inscritos_torneo !== null && (
            <span style={{ marginLeft: '10px', fontWeight: '600', color: '#1976d2' }}>
              📊 {stats.total_inscritos_torneo} futbolistas inscritos en este torneo
            </span>
          )}
        </div>

        {/* Estadísticas de Estados de Usuarios */}
        <div className="stats-grid" style={{ marginBottom: '30px' }}>
          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#c8e6c9' }}>
              <span className="material-icons" style={{ color: '#4caf50' }}>check_circle</span>
            </div>
            <div className="stats-info">
              <h3>Usuarios Completos</h3>
              <div className="stats-value">{stats.usuarios_completos}</div>
              <div className="stats-detail">Con jornadas futuras</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#e1bee7' }}>
              <span className="material-icons" style={{ color: '#9c27b0' }}>new_releases</span>
            </div>
            <div className="stats-info">
              <h3>Primera Inscripción</h3>
              <div className="stats-value">{stats.usuarios_primera_inscripcion || 0}</div>
              <div className="stats-detail">Futbolistas nuevos</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#ffccbc' }}>
              <span className="material-icons" style={{ color: '#ff5722' }}>person_off</span>
            </div>
            <div className="stats-info">
              <h3>Estado 1</h3>
              <div className="stats-value">{stats.usuarios_estado_1}</div>
              <div className="stats-detail">Sin futbolistas registrados</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#fff9c4' }}>
              <span className="material-icons" style={{ color: '#fbc02d' }}>sports_soccer</span>
            </div>
            <div className="stats-info">
              <h3>Estado 2</h3>
              <div className="stats-value">{stats.usuarios_estado_2}</div>
              <div className="stats-detail">{selectedTorneoIdActivity ? 'Sin compras en este torneo' : 'Sin compras'}</div>
            </div>
          </div>

          <div className="stats-card">
            <div className="stats-icon" style={{ backgroundColor: '#ffe0b2' }}>
              <span className="material-icons" style={{ color: '#ff9800' }}>event_busy</span>
            </div>
            <div className="stats-info">
              <h3>Estado 3</h3>
              <div className="stats-value">{stats.usuarios_estado_3}</div>
              <div className="stats-detail">Solo jornadas pasadas</div>
            </div>
          </div>
        </div>

        {/* Buscador */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative', maxWidth: '400px' }}>
            <span
              className="material-icons"
              style={{
                position: 'absolute',
                left: '12px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#666',
                pointerEvents: 'none'
              }}
            >
              search
            </span>
            <input
              type="text"
              placeholder="Buscar por nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 45px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'border-color 0.2s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#2196F3'}
              onBlur={(e) => e.target.style.borderColor = '#ddd'}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666'
                }}
                title="Limpiar búsqueda"
              >
                <span className="material-icons" style={{ fontSize: '20px' }}>close</span>
              </button>
            )}
          </div>
        </div>

        {/* Botones de Vista */}
        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            className={`btn ${userActivityView === 'all' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setUserActivityView('all'); setSelectedSedeFilter(''); }}
          >
            <span className="material-icons">people</span>
            <span>Todos ({stats.total_usuarios_tutores})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'completos' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setUserActivityView('completos'); setSelectedSedeFilter(''); }}
            style={{ backgroundColor: userActivityView === 'completos' ? '#4caf50' : undefined }}
          >
            <span className="material-icons">check_circle</span>
            <span>Completos ({stats.usuarios_completos})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'primera-inscripcion' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setUserActivityView('primera-inscripcion'); setSelectedSedeFilter(''); }}
            style={{ backgroundColor: userActivityView === 'primera-inscripcion' ? '#9c27b0' : undefined }}
          >
            <span className="material-icons">new_releases</span>
            <span>Primera Inscripción ({stats.usuarios_primera_inscripcion || 0})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'estado-1' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setUserActivityView('estado-1'); setSelectedSedeFilter(''); }}
            style={{ backgroundColor: userActivityView === 'estado-1' ? '#ff5722' : undefined }}
          >
            <span className="material-icons">person_off</span>
            <span>Estado 1 ({stats.usuarios_estado_1})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'estado-2' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setUserActivityView('estado-2'); setSelectedSedeFilter(''); }}
            style={{ backgroundColor: userActivityView === 'estado-2' ? '#fbc02d' : undefined }}
          >
            <span className="material-icons">sports_soccer</span>
            <span>Estado 2 ({stats.usuarios_estado_2})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'estado-3' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => { setUserActivityView('estado-3'); setSelectedSedeFilter(''); }}
            style={{ backgroundColor: userActivityView === 'estado-3' ? '#ff9800' : undefined }}
          >
            <span className="material-icons">event_busy</span>
            <span>Estado 3 ({stats.usuarios_estado_3})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'seguro' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setUserActivityView('seguro');
              setSelectedSedeFilter('');
              // Cargar datos de seguro bajo demanda
              if (!seguroData && !seguroLoading) {
                setSeguroLoading(true);
                SeguroService.getFutbolistasConSeguro()
                  .then(response => {
                    setSeguroData(response.data || []);
                    setSeguroLoading(false);
                  })
                  .catch(err => {
                    console.error('Error cargando seguros:', err);
                    setSeguroData([]);
                    setSeguroLoading(false);
                  });
              }
            }}
            style={{ backgroundColor: userActivityView === 'seguro' ? '#1565c0' : undefined }}
          >
            <span className="material-icons">shield</span>
            <span>Seguro ({seguroData ? seguroData.length : '...'})</span>
          </button>
          <button
            className={`btn ${userActivityView === 'captacion' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setUserActivityView('captacion');
              setSelectedSedeFilter('');
              // Cargar datos de captación bajo demanda
              if (!captacionData && !captacionLoading) {
                setCaptacionLoading(true);
                ScoutService.getReporteCaptacion()
                  .then(response => {
                    setCaptacionData(response.data || []);
                    setCaptacionLoading(false);
                  })
                  .catch(err => {
                    console.error('Error cargando captación:', err);
                    setCaptacionData([]);
                    setCaptacionLoading(false);
                  });
              }
            }}
            style={{ backgroundColor: userActivityView === 'captacion' ? '#e65100' : undefined }}
          >
            <span className="material-icons">person_search</span>
            <span>Captación ({captacionData ? captacionData.length : '...'})</span>
          </button>
        </div>

        {/* Filtro de Sede - Solo para Completos, Estado 2 y Estado 3 */}
        {sedeFilterApplies && sedesDisponibles.length > 0 && (
          <div style={{ marginBottom: '15px', padding: '10px 15px', backgroundColor: '#e3f2fd', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <label style={{ fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '14px' }}>
              <span className="material-icons" style={{ fontSize: '18px', color: '#1976d2' }}>location_on</span>
              Filtrar por Sede:
            </label>
            <select
              value={selectedSedeFilter}
              onChange={(e) => setSelectedSedeFilter(e.target.value)}
              style={{
                padding: '6px 10px',
                borderRadius: '4px',
                border: '1px solid #90caf9',
                fontSize: '14px',
                minWidth: '200px',
                backgroundColor: 'white'
              }}
            >
              <option value="">📍 Todas las sedes</option>
              {sedesDisponibles.map(sede => (
                <option key={sede} value={sede}>{sede}</option>
              ))}
            </select>
            {selectedSedeFilter && (
              <button
                onClick={() => setSelectedSedeFilter('')}
                style={{
                  padding: '6px 10px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px'
                }}
              >
                ✕ Limpiar
              </button>
            )}
          </div>
        )}

        {/* Gráfico de Captación */}
        {userActivityView === 'captacion' && captacionData && captacionData.length > 0 && (() => {
          const origenLabels = {
            'scout': 'Scout',
            'recomendacion_futbolista': 'Recomendación',
            'rrss': 'Redes Sociales',
            'web': 'Web / Online',
            'evento_ap8': 'Evento AP8',
            'otro': 'Otro',
            'desconocido': 'Desconocido'
          };
          const origenColors = {
            'scout': '#2196F3',
            'recomendacion_futbolista': '#4caf50',
            'rrss': '#e91e63',
            'web': '#ff9800',
            'evento_ap8': '#9c27b0',
            'otro': '#607d8b',
            'desconocido': '#bdbdbd'
          };
          // Calcular resumen
          const resumen = {};
          captacionData.forEach(f => {
            const o = f.origen_captacion || 'desconocido';
            resumen[o] = (resumen[o] || 0) + 1;
          });
          const total = captacionData.length;
          const entries = Object.entries(resumen).sort((a, b) => b[1] - a[1]);
          const maxVal = entries.length > 0 ? entries[0][1] : 1;

          return (
            <div className="report-card" style={{ marginBottom: '20px' }}>
              <h3 style={{ marginBottom: '20px' }}>Distribución por Origen ({total} futbolistas)</h3>
              <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                {/* Barras horizontales */}
                <div style={{ flex: '1 1 400px', minWidth: '300px' }}>
                  {entries.map(([key, count]) => {
                    const pct = Math.round((count / total) * 100);
                    const barWidth = Math.round((count / maxVal) * 100);
                    return (
                      <div key={key} style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '14px' }}>
                          <span style={{ fontWeight: '600', color: '#333' }}>{origenLabels[key] || key}</span>
                          <span style={{ color: '#666' }}>{count} ({pct}%)</span>
                        </div>
                        <div style={{ height: '24px', backgroundColor: '#f0f0f0', borderRadius: '12px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%',
                            width: `${barWidth}%`,
                            backgroundColor: origenColors[key] || '#999',
                            borderRadius: '12px',
                            transition: 'width 0.6s ease',
                            minWidth: '20px'
                          }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* Donut visual */}
                <div style={{ flex: '0 0 180px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{
                    width: '160px', height: '160px', borderRadius: '50%', position: 'relative',
                    background: `conic-gradient(${entries.map(([key, count], i) => {
                      const startPct = entries.slice(0, i).reduce((s, [, c]) => s + (c / total) * 100, 0);
                      const endPct = startPct + (count / total) * 100;
                      return `${origenColors[key] || '#999'} ${startPct}% ${endPct}%`;
                    }).join(', ')})`
                  }}>
                    <div style={{
                      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                      width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexDirection: 'column'
                    }}>
                      <span style={{ fontSize: '28px', fontWeight: '700', color: '#333' }}>{total}</span>
                      <span style={{ fontSize: '11px', color: '#999' }}>Total</span>
                    </div>
                  </div>
                  {/* Leyenda */}
                  <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '6px', justifyContent: 'center' }}>
                    {entries.map(([key]) => (
                      <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: origenColors[key] || '#999' }} />
                        <span>{origenLabels[key] || key}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Tabla de Datos */}
        <div className="report-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
            <h3>{viewTitle} ({dataToShow.length})</h3>
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* Botón de Enviar Emails - Solo para Estados 1, 2 y 3 */}
              {['estado-1', 'estado-2', 'estado-3'].includes(userActivityView) && (
                <button
                  className="btn"
                  style={{ backgroundColor: '#2196F3', color: 'white' }}
                  onClick={() => handleSendBulkEmails(userActivityView)}
                  disabled={dataToShow.length === 0 || loading}
                >
                  <span className="material-icons">email</span>
                  <span>Enviar Emails ({dataToShow.length})</span>
                </button>
              )}
              <button
                className="btn btn-primary"
                onClick={() => exportToExcel(dataToShow, exportFilename)}
                disabled={dataToShow.length === 0}
              >
                <span className="material-icons">download</span>
                <span>Exportar a Excel</span>
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
            <table className="data-table-small">
              <thead>
                <tr>
                  {dataToShow.length > 0 && Object.keys(dataToShow[0]).filter(k => k !== '_originalData').map((key) => (
                    <th key={key}>{key}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dataToShow.map((row, idx) => (
                  <tr key={idx}>
                    {Object.entries(row).filter(([key]) => key !== '_originalData').map(([key, val], i) => {
                      // Si la columna es "Nº Futbolistas" y el valor es > 0, hacerla clickeable
                      if (key === 'Nº Futbolistas' && typeof val === 'number' && val > 0) {
                        return (
                          <td key={i}>
                            <button
                              onClick={() => handleShowFutbolistas(row._originalData.email)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#2196F3',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                padding: 0,
                                font: 'inherit'
                              }}
                              title="Ver futbolistas"
                            >
                              {val}
                            </button>
                          </td>
                        );
                      }
                      return <td key={i}>{val}</td>;
                    })}
                  </tr>
                ))}
                {dataToShow.length === 0 && (
                  <tr>
                    <td colSpan="10" style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
                      No hay datos para mostrar
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal de Futbolistas */}
        {showFutbolistasModal && selectedTutorFutbolistas && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              padding: '20px'
            }}
            onClick={() => setShowFutbolistasModal(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                padding: '20px',
                maxWidth: '600px',
                width: '100%',
                maxHeight: '80vh',
                overflowY: 'auto',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ margin: 0, color: '#061156' }}>
                  Futbolistas de {selectedTutorFutbolistas.tutorName}
                </h3>
                <button
                  onClick={() => setShowFutbolistasModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '0',
                    width: '30px',
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Cerrar"
                >
                  ×
                </button>
              </div>

              <div style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                <strong>Email:</strong> {selectedTutorFutbolistas.tutorEmail}
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Nombre</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Año Nacimiento</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Torneo</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Inscripciones</th>
                      <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Saldo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTutorFutbolistas.futbolistas.map((futbolista, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '10px' }}>{futbolista.nombre}</td>
                        <td style={{ padding: '10px' }}>{futbolista.ano_nacimiento || 'N/A'}</td>
                        <td style={{ padding: '10px' }}>{futbolista.torneos_nombres || 'N/A'}</td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          {futbolista.total_inscripciones || 0}
                        </td>
                        <td style={{ padding: '10px', textAlign: 'center' }}>
                          <span style={{
                            backgroundColor: futbolista.saldo_minimo_jornadas > 0 ? '#4caf50' : '#f44336',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}>
                            {futbolista.saldo_minimo_jornadas !== null ? futbolista.saldo_minimo_jornadas : 'N/A'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: '20px', textAlign: 'right' }}>
                <button
                  onClick={() => setShowFutbolistasModal(false)}
                  className="btn btn-secondary"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMatchesTab = () => {
    if (!matchesData) {
      return (
        <div className="report-section">
          <div className="loading-message"><p>No hay datos de partidos para el periodo seleccionado.</p></div>
        </div>
      );
    }

    const {
      matchesByMonth = [],
      attendanceRate = [],
      matchesByCategory = [],
      matchesByField = [],
      totalMatches = 0,
    } = matchesData;

    const monthLabels = matchesByMonth.length ? monthNames : monthNames;

    return (
      <div className="report-section">
        <div className="report-grid">
          <div className="report-card">
            <h3>Partidos por Mes</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de partidos por mes</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr>{monthLabels.map((m) => (<th key={m}>{m}</th>))}</tr></thead>
                <tbody><tr>{matchesByMonth.map((v, i) => (<td key={i}>{v}</td>))}</tr></tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Tasa de Asistencia por Mes (%)</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de tasa de asistencia por mes</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr>{monthLabels.map((m) => (<th key={m}>{m}</th>))}</tr></thead>
                <tbody><tr>{attendanceRate.map((v, i) => (<td key={i}>{v}%</td>))}</tr></tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Partidos por Categoría</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de partidos por categoría</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Categoría</th><th>Partidos</th><th>%</th></tr></thead>
                <tbody>
                  {matchesByCategory.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.value}</td>
                      <td>{totalMatches ? Math.round((item.value / totalMatches) * 100) : 0}%</td>
                    </tr>
                  ))}
                  {matchesByCategory.length === 0 && <tr><td colSpan="3" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Partidos por Campo</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de partidos por campo</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Campo</th><th>Partidos</th><th>%</th></tr></thead>
                <tbody>
                  {matchesByField.map((item, index) => (
                    <tr key={index}>
                      <td>{item.name}</td>
                      <td>{item.value}</td>
                      <td>{totalMatches ? Math.round((item.value / totalMatches) * 100) : 0}%</td>
                    </tr>
                  ))}
                  {matchesByField.length === 0 && <tr><td colSpan="3" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  };

  const renderFinancialTab = () => {
    if (!financialData) {
      return (
        <div className="report-section">
          <div className="loading-message"><p>Cargando datos financieros...</p></div>
        </div>
      );
    }

    const { general = {}, revenueByPaymentMethod = [], revenueByType = [], topProducts = [], revenueByMonth = [] } = financialData;

    // Mapea ingresos por mes [Ene..Dic]
    const monthlyRevenue = Array(12).fill(0);
    revenueByMonth.forEach(item => {
      const idx = (item._id?.month ?? item.month ?? 0) - 1;
      if (idx >= 0 && idx < 12) monthlyRevenue[idx] = item.revenue ?? 0;
    });

    const formatPrice = (amount) =>
      new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);

    return (
      <div className="report-section">
        <div className="stats-overview">
          <div className="stat-card">
            <h4>Ingresos Totales</h4>
            <p className="stat-value">{formatPrice(general.completedRevenue)}</p>
            <small>{general.completedTransactions || 0} transacciones completadas</small>
          </div>
          <div className="stat-card">
            <h4>Productos Vendidos</h4>
            <p className="stat-value">{general.totalPurchases || 0}</p>
            <small>{formatPrice(general.purchaseRevenue)}</small>
          </div>
          <div className="stat-card">
            <h4>Equipaciones</h4>
            <p className="stat-value">{general.totalEquipaciones || 0}</p>
            <small>{formatPrice(general.equipacionRevenue)}</small>
          </div>
          <div className="stat-card">
            <h4>Jornadas Activas</h4>
            <p className="stat-value">{general.activePurchases || 0}</p>
            <small>{general.totalSessions || 0} jornadas totales</small>
          </div>
        </div>

        <div className="report-grid">
          <div className="report-card">
            <h3>Ingresos por Mes (€)</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de ingresos por mes</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr>{monthNames.map((m) => (<th key={m}>{m}</th>))}</tr></thead>
                <tbody><tr>{monthlyRevenue.map((v, i) => (<td key={i}>{formatPrice(v)}</td>))}</tr></tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Ingresos por Método de Pago</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de ingresos por método de pago</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Método</th><th>Transacciones</th><th>Ingresos</th><th>%</th></tr></thead>
                <tbody>
                  {revenueByPaymentMethod.map((it, idx) => (
                    <tr key={idx}>
                      <td>
                        {it._id === 'redsys' ? 'Tarjeta/Bizum'
                          : it._id === 'bank_transfer' ? 'Transferencia'
                          : it._id === 'manual' ? 'Manual'
                          : it._id}
                      </td>
                      <td>{it.count}</td>
                      <td>{formatPrice(it.revenue)}</td>
                      <td>{general.completedRevenue ? Math.round((it.revenue / general.completedRevenue) * 100) : 0}%</td>
                    </tr>
                  ))}
                  {revenueByPaymentMethod.length === 0 && <tr><td colSpan="4" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Ingresos por Tipo de Transacción</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de ingresos por tipo</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Tipo</th><th>Transacciones</th><th>Ingresos</th><th>%</th></tr></thead>
                <tbody>
                  {revenueByType.map((it, idx) => (
                    <tr key={idx}>
                      <td>
                        {it._id === 'product_purchase' ? 'Compra de Productos'
                          : it._id === 'wallet_deposit' ? 'Recarga Monedero'
                          : it._id === 'match_payment' ? 'Pago Jornadas'
                          : it._id}
                      </td>
                      <td>{it.count}</td>
                      <td>{formatPrice(it.revenue)}</td>
                      <td>{general.completedRevenue ? Math.round((it.revenue / general.completedRevenue) * 100) : 0}%</td>
                    </tr>
                  ))}
                  {revenueByType.length === 0 && <tr><td colSpan="4" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Productos Más Vendidos</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de productos más vendidos</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Producto</th><th>Código</th><th>Compras</th><th>Ingresos</th></tr></thead>
                <tbody>
                  {topProducts.slice(0, 10).map((p, idx) => (
                    <tr key={idx}>
                      <td>{p.name}</td>
                      <td>{p.code}</td>
                      <td>{p.totalPurchases}</td>
                      <td>{formatPrice(p.totalRevenue)}</td>
                    </tr>
                  ))}
                  {topProducts.length === 0 && <tr><td colSpan="4" style={{textAlign:'center', color:'#666'}}>No hay datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  };

  const renderAchievementsTab = () => {
    if (!achievementsData) {
      return (
        <div className="report-section">
          <div className="loading-message"><p>No hay datos de logros para el periodo seleccionado.</p></div>
        </div>
      );
    }

    const {
      achievementsByCategory = [],
      mostEarnedAchievements = [],
      achievementsByMonth = [],
    } = achievementsData;

    return (
      <div className="report-section">
        <div className="report-grid">
          <div className="report-card">
            <h3>Logros Desbloqueados por Mes</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de logros desbloqueados por mes</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr>{monthNames.map((m) => (<th key={m}>{m}</th>))}</tr></thead>
                <tbody><tr>{achievementsByMonth.map((v, i) => (<td key={i}>{v}</td>))}</tr></tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Logros por Categoría</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de logros por categoría</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Categoría</th><th>Logros</th><th>%</th></tr></thead>
                <tbody>
                  {achievementsByCategory.map((it, idx) => {
                    const total = achievementsByCategory.reduce((acc, x) => acc + (x.value || 0), 0) || 0;
                    return (
                      <tr key={idx}>
                        <td>{it.name}</td>
                        <td>{it.value}</td>
                        <td>{total ? Math.round((it.value / total) * 100) : 0}%</td>
                      </tr>
                    );
                  })}
                  {achievementsByCategory.length === 0 && <tr><td colSpan="3" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Logros Más Obtenidos</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de logros más obtenidos</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Logro</th><th>Usuarios</th></tr></thead>
                <tbody>
                  {mostEarnedAchievements.map((it, idx) => (
                    <tr key={idx}>
                      <td>{it.name}</td>
                      <td>{it.value}</td>
                    </tr>
                  ))}
                  {mostEarnedAchievements.length === 0 && <tr><td colSpan="2" style={{textAlign:'center', color:'#666'}}>Sin datos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="report-card">
            <h3>Distribución de XP</h3>
            <div className="chart-placeholder"><div className="placeholder-text">Gráfico de distribución de XP</div></div>
            <div className="data-table-small">
              <table>
                <thead><tr><th>Rango XP</th><th>Usuarios</th><th>%</th></tr></thead>
                <tbody>
                  {/* Sustituir por datos reales cuando tengas el endpoint */}
                  <tr><td>0-50 XP</td><td>—</td><td>—</td></tr>
                  <tr><td>51-100 XP</td><td>—</td><td>—</td></tr>
                  <tr><td>101-200 XP</td><td>—</td><td>—</td></tr>
                  <tr><td>201+ XP</td><td>—</td><td>—</td></tr>
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>
    );
  };

  const handleDescargarNuevosFutbolistas = async () => {
    try {
      console.log('🔍 Iniciando descarga de nuevos futbolistas...');
      console.log('📍 Torneo seleccionado:', selectedTorneoId || 'TODOS');
      toast.info('Generando documento de Nuevos Futbolistas...');

      const response = await reportService.getNuevosFutbolistas(selectedTorneoId || null);
      console.log('📦 Respuesta del servidor:', response);

      if (!response.data || response.data.data.length === 0) {
        console.log('⚠️ No hay datos para exportar');
        toast.warning('No hay nuevos futbolistas para exportar');
        return;
      }

      const datos = response.data.data;
      console.log('✅ Datos recibidos:', datos.length, 'futbolistas');

      // Crear hoja de Excel
      const worksheet = XLSX.utils.json_to_sheet(datos.map(item => ({
        'NOMBRE TUTOR': item.tutor_nombre,
        'TELÉFONO': item.tutor_telefono,
        'EMAIL': item.tutor_email,
        'FUTBOLISTA': item.futbolista_nombre,
        'AÑO NACIMIENTO': item.futbolista_ano_nacimiento,
        'SEDE PREFERIDA': item.sede_preferida || 'Sin sede',
        'SEDE JORNADAS': item.sede_jornadas || 'Sin jornadas',
        'TALLA ROPA': item.talla_ropa,
        'SEGURO': item.seguro,
        'JORNADA': item.jornada
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Nuevos Futbolistas');

      // Descargar archivo
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Nuevos_Futbolistas_${fecha}.xlsx`);

      console.log('✅ Archivo Excel generado correctamente');
      toast.success(`✅ Documento generado: ${datos.length} nuevos futbolistas`);
    } catch (error) {
      console.error('❌ Error al descargar nuevos futbolistas:', error);
      console.error('❌ Detalles del error:', error.response?.data || error.message);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDescargarEstadisticasJornadas = async () => {
    try {
      console.log('🔍 Iniciando descarga de estadísticas de jornadas...');
      console.log('📍 Torneo seleccionado:', selectedTorneoId || 'TODOS');
      toast.info('Generando documento de Estadísticas de Jornadas...');

      const response = await reportService.getEstadisticasJornadas(selectedTorneoId || null);
      console.log('📦 Respuesta del servidor:', response);

      if (!response.data || response.data.data.length === 0) {
        console.log('⚠️ No hay datos para exportar');
        toast.warning('No hay jornadas disputadas para exportar');
        return;
      }

      const datos = response.data.data;
      console.log('✅ Datos recibidos:', datos.length, 'jornadas');

      // Crear hoja de Excel
      const worksheet = XLSX.utils.json_to_sheet(datos.map(item => ({
        'JORNADA': `J${item.jornada_numero}`,
        'FECHA': new Date(item.jornada_fecha).toLocaleDateString('es-ES'),
        'TORNEO': item.torneo_nombre,
        'SEDE': item.sede_nombre,
        'TEMPORADA': item.temporada_nombre,
        'TOTAL FUTBOLISTAS': item.total_futbolistas,
        'NUEVOS': item.futbolistas_nuevos,
        '% NUEVOS': `${item.porcentaje_nuevos}%`,
        'RECURRENTES': item.futbolistas_recurrentes,
        '% RECURRENTES': `${item.porcentaje_recurrentes}%`,
        'INVITADOS': item.futbolistas_invitados,
        '% INVITADOS': `${item.porcentaje_invitados}%`
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Estadísticas Jornadas');

      // Descargar archivo
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Estadisticas_Jornadas_${fecha}.xlsx`);

      console.log('✅ Archivo Excel generado correctamente');
      toast.success(`✅ Documento generado: ${datos.length} jornadas analizadas`);
    } catch (error) {
      console.error('❌ Error al descargar estadísticas de jornadas:', error);
      console.error('❌ Detalles del error:', error.response?.data || error.message);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDescargarEstadisticasPromos = async () => {
    try {
      console.log('🔍 Iniciando descarga de estadísticas de promos...');
      console.log('📍 Torneo seleccionado:', selectedTorneoId || 'TODOS');
      toast.info('Generando documento de Estadísticas de Promos...');

      const response = await reportService.getEstadisticasPromos(selectedTorneoId || null);
      console.log('📦 Respuesta del servidor:', response);

      if (!response.data || response.data.data.length === 0) {
        console.log('⚠️ No hay datos para exportar');
        toast.warning('No hay paquetes promocionales para exportar');
        return;
      }

      const datos = response.data.data;
      console.log('✅ Datos recibidos:', datos.length, 'paquetes promocionales');

      // Crear hoja de Excel
      const worksheet = XLSX.utils.json_to_sheet(datos.map(item => ({
        'PAQUETE': item.nombre_paquete,
        'TOTAL COMPRAS': item.total_compras,
        'COMPRAS CON AMIGO VERIFICADO': item.compras_con_amigo_verificado,
        '% CON AMIGO': item.total_compras > 0
          ? `${((item.compras_con_amigo_verificado / item.total_compras) * 100).toFixed(2)}%`
          : '0%'
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Estadísticas Promos');

      // Descargar archivo
      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Estadisticas_Promos_${fecha}.xlsx`);

      console.log('✅ Archivo Excel generado correctamente');
      toast.success(`✅ Documento generado: ${datos.length} paquetes promocionales`);
    } catch (error) {
      console.error('❌ Error al descargar estadísticas de promos:', error);
      console.error('❌ Detalles del error:', error.response?.data || error.message);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const formatearFechaExcel = (fecha) =>
    fecha ? new Date(fecha).toLocaleDateString('es-ES') : '—';

  const handleDescargarPrimeraExperiencia = async () => {
    try {
      toast.info('Generando listado de Primera Experiencia...');
      const response = await reportService.getComprasPrimeraExperiencia(selectedTorneoId || null);

      const datos = response.data?.data || [];
      if (datos.length === 0) {
        toast.warning('No hay compras de Primera Experiencia para exportar');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(datos.map(item => ({
        'NOMBRE TUTOR': item.tutor_nombre,
        'TELÉFONO': item.tutor_telefono,
        'EMAIL': item.tutor_email,
        'FUTBOLISTA': item.futbolista_nombre,
        'AÑO NACIMIENTO': item.futbolista_ano_nacimiento,
        'SEDE': item.sede,
        'TORNEO': item.torneo,
        'FECHA COMPRA': formatearFechaExcel(item.fecha_compra),
        '¿DISFRUTADA?': item.disfrutada,
        'FECHA DISFRUTADA': formatearFechaExcel(item.fecha_disfrutada),
        'PRÓXIMA JORNADA': formatearFechaExcel(item.proxima_jornada),
        'JORNADAS JUGADAS': item.jornadas_participadas
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Primera Experiencia');

      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Primera_Experiencia_${fecha}.xlsx`);

      const pendientes = datos.filter(d => d.disfrutada === 'NO').length;
      toast.success(`✅ ${datos.length} compras (${pendientes} sin disfrutar)`);
    } catch (error) {
      console.error('❌ Error al descargar Primera Experiencia:', error);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDescargarComprasPacks = async () => {
    try {
      toast.info('Generando listado de Packs...');
      const response = await reportService.getComprasPacks(selectedTorneoId || null);

      const datos = response.data?.data || [];
      if (datos.length === 0) {
        toast.warning('No hay compras de packs para exportar');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(datos.map(item => ({
        'FECHA COMPRA': formatearFechaExcel(item.fecha_compra),
        'NOMBRE TUTOR': item.tutor_nombre,
        'TELÉFONO': item.tutor_telefono,
        'EMAIL': item.tutor_email,
        'FUTBOLISTA': item.futbolista_nombre,
        'AÑO NACIMIENTO': item.futbolista_ano_nacimiento,
        'SEDE': item.sede,
        'TEMPORADA': item.temporada,
        'TORNEO': item.torneo,
        'PACK': item.pack,
        'Nº PACK': item.orden_pack,
        'JORNADAS DEL PACK': item.jornadas_del_pack,
        'IMPORTE': item.importe,
        'CONSUMIDAS': item.jornadas_consumidas,
        'PENDIENTES': item.jornadas_pendientes,
        'ESTADO': item.estado,
        'PRÓXIMA JORNADA': formatearFechaExcel(item.proxima_jornada),
        'EQUIPACIÓN': item.equipacion,
        'SEGURO': item.seguro,
        'TOTAL JUGADAS EN TORNEO': item.total_jugadas_torneo,
        'JUGÓ SIN COMPRAR': item.jugo_sin_comprar,
        'SALDO EN SISTEMA': item.saldo_guardado
      })));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Compras de Packs');

      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Compras_Packs_${fecha}.xlsx`);

      const sinEmpezar = datos.filter(d => d.estado === 'Sin empezar').length;
      const enCurso = datos.filter(d => d.estado === 'En curso').length;
      toast.success(`✅ ${datos.length} compras — ${sinEmpezar} sin empezar, ${enCurso} en curso`);
    } catch (error) {
      console.error('❌ Error al descargar compras de packs:', error);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDescargarListadoUsuarios = async () => {
    try {
      toast.info('Generando Listado de Usuarios...');
      const params = {};
      if (selectedTorneoId) params.torneo_id = selectedTorneoId;
      const response = await reportService.getUserActivityDetailed(params);

      if (!response.data || !response.data.data) {
        toast.warning('No hay datos para exportar');
        return;
      }

      const usuarios = response.data.data.todos_usuarios || [];
      if (usuarios.length === 0) {
        toast.warning('No hay usuarios para exportar');
        return;
      }

      // Una fila por cada dupla tutor-futbolista
      const excelData = usuarios.flatMap(u => {
        const lista = u.futbolistas_lista || [];
        const buildRow = (fut) => ({
          Nombre: u.name || 'N/A',
          Email: u.email || 'N/A',
          Teléfono: u.phone || 'N/A',
          Estado: u.estado || 'N/A',
          Futbolista: fut.nombre || '',
          'Año Nacimiento': fut.ano_nacimiento || '',
          'Posición': fut.posicion || '',
          'Sede Preferida': fut.sede_por_defecto || 'Sin sede',
          Torneo: fut.torneos || 'N/A',
          'Sedes Jornadas Futuras': fut.sedes_futuras || 'N/A',
          'Sedes Jornadas Pasadas': fut.sedes_pasadas || 'N/A',
          'Jornadas Disputadas': fut.jornadas_disputadas || '',
          'Nº Inscripciones': u.num_inscripciones !== undefined ? u.num_inscripciones : 'N/A',
          'Jornadas Restantes': u.jornadas_restantes !== null && u.jornadas_restantes !== undefined ? u.jornadas_restantes : 'N/A',
          'Saldo Mínimo': u.saldo_minimo !== null && u.saldo_minimo !== undefined ? u.saldo_minimo : 'N/A',
          'Última Compra': fut.ultima_compra ? new Date(fut.ultima_compra).toLocaleDateString('es-ES') : 'N/A',
          'Fin Inscripción': u.fin_inscripcion ? new Date(u.fin_inscripcion).toLocaleDateString('es-ES') : 'N/A',
          'Fecha Registro': u.createdAt ? new Date(u.createdAt).toLocaleDateString('es-ES') : 'N/A',
        });

        if (lista.length === 0) {
          return [buildRow({})];
        }
        return lista.map(f => buildRow(f));
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Listado de Usuarios');

      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Listado_Usuarios_${fecha}.xlsx`);

      toast.success(`✅ Documento generado: ${excelData.length} usuarios`);
    } catch (error) {
      console.error('❌ Error al descargar listado de usuarios:', error);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDescargarSeguro = async () => {
    try {
      toast.info('Generando listado de futbolistas con seguro...');
      const response = await SeguroService.getFutbolistasConSeguro();

      if (!response.data || response.data.length === 0) {
        toast.warning('No hay futbolistas con seguro activo');
        return;
      }

      const excelData = response.data.map(f => ({
        'Futbolista': f.futbolista_nombre || 'N/A',
        'Fecha Nacimiento': f.fecha_nacimiento ? new Date(f.fecha_nacimiento).toLocaleDateString('es-ES') : (f.ano_nacimiento || 'N/A'),
        'Sede Preferida': f.sede_preferida || 'N/A',
        'Tutor': f.tutor_nombre || 'N/A',
        'Email Tutor': f.tutor_email || 'N/A',
        'Teléfono Tutor': f.tutor_telefono || 'N/A',
        'Fecha Compra': f.fecha_compra ? new Date(f.fecha_compra).toLocaleDateString('es-ES') : 'N/A',
        'Inicio Cobertura': f.fecha_inicio_cobertura ? new Date(f.fecha_inicio_cobertura).toLocaleDateString('es-ES') : 'N/A',
        'Fin Cobertura': f.fecha_fin_cobertura ? new Date(f.fecha_fin_cobertura).toLocaleDateString('es-ES') : 'N/A',
        'Precio Pagado': f.precio_pagado != null ? `${parseFloat(f.precio_pagado).toFixed(2)} €` : 'N/A'
      }));

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Seguros Activos');

      const fecha = new Date().toISOString().split('T')[0];
      XLSX.writeFile(workbook, `Futbolistas_Con_Seguro_${fecha}.xlsx`);

      toast.success(`✅ Documento generado: ${excelData.length} futbolistas con seguro`);
    } catch (error) {
      console.error('❌ Error al descargar listado de seguros:', error);
      toast.error(`Error al generar el documento: ${error.response?.data?.message || error.message}`);
    }
  };

  const renderDocumentsTab = () => {
    return (
      <div className="tab-content">
        <div className="section-header">
          <h3>📄 Documentos y Exportaciones</h3>
          <p className="text-muted">Genera y descarga documentos en formato Excel</p>
        </div>

        {/* Filtro de Torneo */}
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <label htmlFor="torneo-filter" style={{ fontWeight: '600', marginRight: '10px', display: 'inline-block' }}>
            🏆 Filtrar por Torneo:
          </label>
          <select
            id="torneo-filter"
            value={selectedTorneoId}
            onChange={(e) => setSelectedTorneoId(e.target.value)}
            style={{
              padding: '8px 12px',
              borderRadius: '4px',
              border: '1px solid #ced4da',
              fontSize: '14px',
              minWidth: '250px'
            }}
          >
            <option value="">📋 Todos los torneos</option>
            {torneos.map(torneo => (
              <option key={torneo.id} value={torneo.id}>
                {torneo.nombre}
              </option>
            ))}
          </select>
          {selectedTorneoId && (
            <button
              onClick={() => setSelectedTorneoId('')}
              style={{
                marginLeft: '10px',
                padding: '8px 12px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              ✖ Limpiar filtro
            </button>
          )}
        </div>

        <div className="documents-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px', marginTop: '20px' }}>

          {/* Documento: Primera Experiencia */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#f39c12', marginRight: '12px' }}>bolt</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Primera Experiencia</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Clientes que compraron el pack Primera Experiencia, indicando si ya la han disfrutado
              o siguen pendientes de jugar su jornada
            </p>
            <button
              className="btn btn-warning btn-sm"
              onClick={handleDescargarPrimeraExperiencia}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

          {/* Documento: Compras de Packs */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#196ca8', marginRight: '12px' }}>inventory_2</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Compras de Packs</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Una fila por compra de pack de más de una jornada en la <strong>temporada
              activa</strong>, con su seguimiento: jugadas desde la compra, pendientes y estado
            </p>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleDescargarComprasPacks}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

          {/* Documento: Nuevos Futbolistas */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#28a745', marginRight: '12px' }}>person_add</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Nuevos Futbolistas</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Tutores que compraron por primera vez para un futbolista que aún no ha disputado ninguna jornada
            </p>
            <button
              className="btn btn-success btn-sm"
              onClick={handleDescargarNuevosFutbolistas}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

          {/* Documento: Estadísticas de Jornadas */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#007bff', marginRight: '12px' }}>analytics</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Estadísticas de Jornadas</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Porcentaje de futbolistas nuevos vs. recurrentes en cada jornada disputada
            </p>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleDescargarEstadisticasJornadas}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

          {/* Documento: Estadísticas de Promos */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#ff6b6b', marginRight: '12px' }}>local_offer</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Estadísticas de Promos</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Número de compras por paquete promocional y uso de Promo Amigo con amigo verificado
            </p>
            <button
              className="btn btn-danger btn-sm"
              onClick={handleDescargarEstadisticasPromos}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

          {/* Documento: Listado de Usuarios */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#6f42c1', marginRight: '12px' }}>people</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Listado de Usuarios</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Todos los tutores con detalle de futbolistas (nombre:año_nacimiento). Excluye usuarios silenciados.
            </p>
            <button
              className="btn btn-sm"
              onClick={handleDescargarListadoUsuarios}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#6f42c1', color: '#fff', border: 'none' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

          {/* Documento: Futbolistas con Seguro */}
          <div className="document-card" style={{
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '20px',
            backgroundColor: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
          }}
          >
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '12px' }}>
              <span className="material-icons" style={{ fontSize: '32px', color: '#1565c0', marginRight: '12px' }}>shield</span>
              <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>Futbolistas con Seguro</h5>
            </div>
            <p style={{ fontSize: '14px', color: '#6c757d', marginBottom: '16px', minHeight: '60px' }}>
              Listado de futbolistas con seguro voluntario de accidentes activo, incluyendo datos de tutor y cobertura
            </p>
            <button
              className="btn btn-sm"
              onClick={handleDescargarSeguro}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', backgroundColor: '#1565c0', color: '#fff', border: 'none' }}
            >
              <span className="material-icons" style={{ fontSize: '18px' }}>download</span>
              Descargar Excel
            </button>
          </div>

        </div>
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'overview': return renderOverviewTab();
      case 'userActivity': return renderUserActivityTab();
      case 'matches': return renderMatchesTab();
      case 'financial': return renderFinancialTab();
      case 'achievements': return renderAchievementsTab();
      case 'documents': return renderDocumentsTab();
      default: return renderOverviewTab();
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Informes" subtitle="Cargando...">
        <div className="loading">Cargando datos de informes...</div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="Informes" subtitle="Error">
        <div className="error">{error}</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Informes" subtitle="Análisis y estadísticas de la plataforma">
      <div className="reports-header">
        <div className="reports-tabs">
          <button className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`} onClick={() => handleTabChange('overview')}>
            <span className="material-icons">dashboard</span><span>Resumen</span>
          </button>
          <button className={`tab-button ${activeTab === 'userActivity' ? 'active' : ''}`} onClick={() => handleTabChange('userActivity')}>
            <span className="material-icons">people</span><span>Actividad de Usuarios</span>
          </button>
          <button className={`tab-button ${activeTab === 'matches' ? 'active' : ''}`} onClick={() => handleTabChange('matches')}>
            <span className="material-icons">sports_soccer</span><span>Partidos</span>
          </button>
          <button className={`tab-button ${activeTab === 'financial' ? 'active' : ''}`} onClick={() => handleTabChange('financial')}>
            <span className="material-icons">euro</span><span>Financiero</span>
          </button>
          <button className={`tab-button ${activeTab === 'achievements' ? 'active' : ''}`} onClick={() => handleTabChange('achievements')}>
            <span className="material-icons">emoji_events</span><span>Logros</span>
          </button>
          <button className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`} onClick={() => handleTabChange('documents')}>
            <span className="material-icons">description</span><span>Documentos</span>
          </button>
        </div>

        <div className="reports-filters">
          <div className="filter-group">
            <label htmlFor="dateRange">Periodo:</label>
            <select id="dateRange" value={dateRange} onChange={handleDateRangeChange} className="form-control">
              <option value="week">Última semana</option>
              <option value="month">Último mes</option>
              <option value="quarter">Último trimestre</option>
              <option value="year">Último año</option>
              <option value="all">Todo el tiempo</option>
            </select>
          </div>

        </div>
      </div>

      <div className="reports-content">{renderActiveTab()}</div>
    </AdminLayout>
  );
};

export default Reports;

